// ╔══════════════════════════════════════════════════════════════╗
// ║  App shell — Sidebar + TopBar + Outlet                        ║
// ║  Responsive: collapsing sidebar on tablet, drawer on mobile   ║
// ╚══════════════════════════════════════════════════════════════╝

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '../lib/auth.jsx';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import Icon from './Icon.jsx';
import IdleTimeout from './IdleTimeout.jsx';
import { Avatar, Badge } from './ui.jsx';

const NAV_BY_ROLE = {
  admin: [
    { id: '/', l: 'لوحة التحكّم', i: 'dashboard', section: 'main' },
    { id: '/employees', l: 'الموظفون', i: 'users', section: 'main' },
    { id: '/attendance', l: 'سجل الحضور', i: 'fingerprint', section: 'main' },
    { id: '/fingerprint', l: 'رفع البصمة', i: 'fingerprint', section: 'main' },
    { id: '/uploads', l: 'الرفع والاستيراد', i: 'upload', section: 'main' },
    { id: '/payroll', l: 'الرواتب', i: 'wallet', section: 'main' },
    { id: '/payslips', l: 'كشوف الرواتب', i: 'file', section: 'main' },
    { id: '/reports', l: 'التقارير', i: 'bar', section: 'main' },
    { id: '/production/workers', l: 'عمال الإنتاج', i: 'users', section: 'production' },
    { id: '/production/stages', l: 'إدارة المراحل', i: 'layers', section: 'production' },
    { id: '/production/history', l: 'سجل الإدخالات', i: 'log', section: 'production' },
    { id: '/production/review', l: 'مراجعة الإنتاج', i: 'check', section: 'production' },
    { id: '/audit', l: 'سجل التدقيق', i: 'log', section: 'admin' },
    { id: '/settings', l: 'الإعدادات', i: 'settings', section: 'admin' },
  ],
  acc: [
    { id: '/', l: 'لوحة المحاسب', i: 'dashboard', section: 'main' },
    { id: '/employees', l: 'الموظفون', i: 'users', section: 'main' },
    { id: '/attendance', l: 'سجل الحضور', i: 'fingerprint', section: 'main' },
    { id: '/fingerprint', l: 'رفع البصمة', i: 'fingerprint', section: 'main' },
    { id: '/uploads', l: 'الرفع والاستيراد', i: 'upload', section: 'main' },
    { id: '/payroll', l: 'دورات الرواتب', i: 'wallet', section: 'main' },
    { id: '/payslips', l: 'كشوف الرواتب', i: 'file', section: 'main' },
    { id: '/reports', l: 'التقارير', i: 'bar', section: 'main' },
    { id: '/production/review', l: 'مراجعة الإنتاج', i: 'check', section: 'production' },
    { id: '/production/history', l: 'سجل الإدخالات', i: 'log', section: 'production' },
    { id: '/settings', l: 'الإعدادات', i: 'settings', section: 'admin' },
  ],
  eng: [
    { id: '/', l: 'نظرة عامة', i: 'dashboard', section: 'main' },
    { id: '/production/entry', l: 'إدخال إنتاج اليوم', i: 'plus', section: 'main' },
    { id: '/production/workers', l: 'إدارة العمال', i: 'users', section: 'main' },
    { id: '/production/stages', l: 'إدارة المراحل', i: 'layers', section: 'main' },
    { id: '/production/history', l: 'سجل الإدخالات', i: 'log', section: 'main' },
    { id: '/settings', l: 'الإعدادات', i: 'settings', section: 'admin' },
  ],
  // Single-page role — only fingerprint upload.
  att: [
    { id: '/fingerprint', l: 'رفع البصمة', i: 'fingerprint', section: 'main' },
  ],
};

const ROLE_BADGE = {
  admin: 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300',
  acc: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  eng: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  att: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState(
    () => localStorage.getItem('hindam_theme') || 'dark',
  );
  const location = useLocation();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('hindam_theme', theme);
  }, [theme]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (!user) return null;
  const items = NAV_BY_ROLE[user.role] || [];

  return (
    <div className="min-h-screen flex bg-ink-50/40 dark:bg-ink-950/40">
      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        items={items}
        role={user.role}
        user={user}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar
          user={user}
          theme={theme}
          setTheme={setTheme}
          onLogout={logout}
          onOpenMobile={() => setMobileOpen(true)}
        />
        <main className="dot-grid flex-1">
          <Outlet />
        </main>
      </div>
      <IdleTimeout />
    </div>
  );
}

// ─── Sidebar ───
function Sidebar({
  items,
  role,
  user,
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
}) {
  const mainItems = items.filter((i) => i.section === 'main');
  const hrItems = items.filter((i) => i.section === 'hr');
  const prodItems = items.filter((i) => i.section === 'production');
  const adminItems = items.filter((i) => i.section === 'admin');

  return (
    <aside
      className={clsx(
        'shrink-0 border-l border-ink-100 dark:border-ink-800 bg-white dark:bg-ink-900 flex flex-col',
        'fixed lg:sticky inset-y-0 right-0 z-50 lg:z-auto transition-transform lg:transition-[width]',
        mobileOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0',
        collapsed ? 'w-[76px]' : 'w-[260px]',
        'top-0 h-screen',
      )}
    >
      <div className="p-4 flex items-center justify-between">
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <img
              src="/logo.jpg"
              alt=""
              className="w-9 h-9 rounded-lg ring-1 ring-brand-700/30 object-cover"
            />
            <div className="leading-tight">
              <div className="font-extrabold text-[14px] tracking-tight">
                رداء <span className="text-brand-500">هندام</span>
              </div>
              <div className="text-[9px] text-ink-400 font-medium tracking-[0.18em] uppercase">
                Integrated System v2
              </div>
            </div>
          </div>
        ) : (
          <img
            src="/logo.jpg"
            className="w-9 h-9 rounded-lg ring-1 ring-brand-700/30 mx-auto object-cover"
          />
        )}
        <button
          onClick={() => {
            // On mobile, the close button closes the drawer
            if (window.innerWidth < 1024) setMobileOpen(false);
            else setCollapsed((c) => !c);
          }}
          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-400"
        >
          <Icon name={collapsed ? 'chevR' : 'chevL'} size={16} />
        </button>
      </div>

      {/* User badge */}
      {!collapsed && (
        <div className="px-4 pb-3">
          <div className={clsx('flex items-center gap-2 px-3 py-2.5 rounded-xl', ROLE_BADGE[role])}>
            <Avatar name={user.name} size={28} />
            <div className="flex-1 min-w-0 leading-tight">
              <div className="text-[12px] font-bold truncate">{user.name}</div>
              <div className="text-[10px] opacity-80">{user.title}</div>
            </div>
          </div>
        </div>
      )}

      <div className="px-3 mt-1 flex-1 overflow-y-auto no-scrollbar pb-4">
        {!collapsed && (
          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-bold px-2 mb-2">
            القائمة الرئيسية
          </div>
        )}
        <ul className="space-y-1">
          {mainItems.map((it) => (
            <NavItem key={it.id} it={it} collapsed={collapsed} />
          ))}
        </ul>

        {hrItems.length > 0 && (
          <>
            {!collapsed && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-bold px-2 mt-5 mb-2">
                الموارد البشرية
              </div>
            )}
            <ul className="space-y-1">
              {hrItems.map((it) => (
                <NavItem key={it.id} it={it} collapsed={collapsed} />
              ))}
            </ul>
          </>
        )}

        {prodItems.length > 0 && (
          <>
            {!collapsed && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-bold px-2 mt-5 mb-2">
                الإنتاج
              </div>
            )}
            <ul className="space-y-1">
              {prodItems.map((it) => (
                <NavItem key={it.id} it={it} collapsed={collapsed} />
              ))}
            </ul>
          </>
        )}

        {adminItems.length > 0 && (
          <>
            {!collapsed && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-bold px-2 mt-5 mb-2">
                الإدارة
              </div>
            )}
            <ul className="space-y-1">
              {adminItems.map((it) => (
                <NavItem key={it.id} it={it} collapsed={collapsed} />
              ))}
            </ul>
          </>
        )}
      </div>
    </aside>
  );
}

function NavItem({ it, collapsed }) {
  return (
    <li>
      <NavLink
        to={it.id}
        end={it.id === '/'}
        className={({ isActive }) =>
          clsx(
            'w-full flex items-center gap-3 h-10 rounded-xl text-sm font-semibold transition',
            collapsed ? 'justify-center px-2' : 'px-3',
            isActive
              ? 'bg-brand-500 text-ink-950 shadow-soft'
              : 'text-ink-600 dark:text-ink-200 hover:bg-ink-100 dark:hover:bg-ink-800',
          )
        }
      >
        <Icon name={it.i} size={17} />
        {!collapsed && <span className="flex-1 text-start">{it.l}</span>}
      </NavLink>
    </li>
  );
}

// ─── TopBar ───
function TopBar({ user, theme, setTheme, onLogout, onOpenMobile }) {
  const navigate = useNavigate();
  const [openUser, setOpenUser] = useState(false);

  // Poll unread notifications every 60s
  const { data: unreadData } = useQuery({
    queryKey: ['notif-unread'],
    queryFn: () => api.get('/notifications/unread-count').then((r) => r.data),
    refetchInterval: 60_000,
  });
  const unread = unreadData?.unread ?? 0;

  return (
    <header className="sticky top-0 z-30 bg-white/85 dark:bg-ink-900/85 backdrop-blur border-b border-ink-100 dark:border-ink-800">
      <div className="flex items-center gap-3 px-4 lg:px-8 h-16">
        {/* Mobile menu button */}
        <button
          onClick={onOpenMobile}
          className="lg:hidden w-9 h-9 grid place-items-center rounded-xl hover:bg-ink-100 dark:hover:bg-ink-800"
        >
          <Icon name="more" size={18} />
        </button>

        <div className="ms-auto flex items-center gap-2">
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="h-9 w-9 grid place-items-center rounded-xl ring-1 ring-ink-200 dark:ring-ink-700 hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-500"
            title={theme === 'dark' ? 'الوضع النهاري' : 'الوضع الليلي'}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={16} />
          </button>

          <button
            className="h-9 w-9 grid place-items-center rounded-xl ring-1 ring-ink-200 dark:ring-ink-700 hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-500 relative"
          >
            <Icon name="bell" size={16} />
            {unread > 0 && (
              <span className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-500 text-ink-950 text-[10px] font-extrabold grid place-items-center ring-2 ring-white dark:ring-ink-900">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setOpenUser((v) => !v)}
              className="h-9 ps-2 pe-3 inline-flex items-center gap-2 rounded-xl ring-1 ring-ink-200 dark:ring-ink-700 hover:bg-ink-100 dark:hover:bg-ink-800"
            >
              <Avatar name={user.name} size={26} />
              <div className="text-start hidden lg:block leading-tight">
                <div className="text-xs font-bold">{user.name}</div>
                <div className="text-[10px] text-ink-400">{user.title}</div>
              </div>
              <Icon name="chevD" size={13} className="text-ink-400" />
            </button>
            {openUser && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setOpenUser(false)} />
                <div className="absolute end-0 mt-2 w-64 rounded-2xl bg-white dark:bg-ink-900 ring-1 ring-ink-200 dark:ring-ink-700 shadow-pop overflow-hidden z-50 fade-up">
                  <div className="p-4 bg-gradient-to-bl from-brand-100 to-white dark:from-brand-900/30 dark:to-ink-900">
                    <div className="flex items-center gap-3">
                      <Avatar name={user.name} size={42} />
                      <div className="min-w-0">
                        <div className="font-bold text-sm truncate">{user.name}</div>
                        <div className="text-[11px] text-ink-400 truncate">{user.email}</div>
                        <Badge
                          tone={user.role === 'admin' ? 'brand' : user.role === 'acc' ? 'success' : 'info'}
                          className="mt-1"
                        >
                          {user.title}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <ul className="p-1">
                    <li>
                      <button
                        onClick={() => {
                          setOpenUser(false);
                          navigate('/change-password');
                        }}
                        className="w-full flex items-center gap-3 px-3 h-9 rounded-lg text-sm hover:bg-ink-100 dark:hover:bg-ink-800"
                      >
                        <Icon name="shield" size={14} /> تغيير كلمة المرور
                      </button>
                    </li>
                    <li className="border-t border-ink-100 dark:border-ink-800 mt-1 pt-1">
                      <button
                        onClick={onLogout}
                        className="w-full flex items-center gap-3 px-3 h-9 rounded-lg text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                      >
                        <Icon name="x" size={14} /> تسجيل الخروج
                      </button>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
